/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace rayleigh
*/
var rayleigh = {};

/**
* @name cdf
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/cdf}
*/
import cdf from './../../../../base/dists/rayleigh/cdf';
setReadOnly( rayleigh, 'cdf', cdf );

/**
* @name Rayleigh
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/ctor}
*/
import Rayleigh from './../../../../base/dists/rayleigh/ctor';
setReadOnly( rayleigh, 'Rayleigh', Rayleigh );

/**
* @name entropy
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/entropy}
*/
import entropy from './../../../../base/dists/rayleigh/entropy';
setReadOnly( rayleigh, 'entropy', entropy );

/**
* @name kurtosis
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/kurtosis}
*/
import kurtosis from './../../../../base/dists/rayleigh/kurtosis';
setReadOnly( rayleigh, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/logcdf}
*/
import logcdf from './../../../../base/dists/rayleigh/logcdf';
setReadOnly( rayleigh, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/logpdf}
*/
import logpdf from './../../../../base/dists/rayleigh/logpdf';
setReadOnly( rayleigh, 'logpdf', logpdf );

/**
* @name mean
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/mean}
*/
import mean from './../../../../base/dists/rayleigh/mean';
setReadOnly( rayleigh, 'mean', mean );

/**
* @name median
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/median}
*/
import median from './../../../../base/dists/rayleigh/median';
setReadOnly( rayleigh, 'median', median );

/**
* @name mgf
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/mgf}
*/
import mgf from './../../../../base/dists/rayleigh/mgf';
setReadOnly( rayleigh, 'mgf', mgf );

/**
* @name mode
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/mode}
*/
import mode from './../../../../base/dists/rayleigh/mode';
setReadOnly( rayleigh, 'mode', mode );

/**
* @name pdf
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/pdf}
*/
import pdf from './../../../../base/dists/rayleigh/pdf';
setReadOnly( rayleigh, 'pdf', pdf );

/**
* @name quantile
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/quantile}
*/
import quantile from './../../../../base/dists/rayleigh/quantile';
setReadOnly( rayleigh, 'quantile', quantile );

/**
* @name skewness
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/skewness}
*/
import skewness from './../../../../base/dists/rayleigh/skewness';
setReadOnly( rayleigh, 'skewness', skewness );

/**
* @name stdev
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/stdev}
*/
import stdev from './../../../../base/dists/rayleigh/stdev';
setReadOnly( rayleigh, 'stdev', stdev );

/**
* @name variance
* @memberof rayleigh
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/rayleigh/variance}
*/
import variance from './../../../../base/dists/rayleigh/variance';
setReadOnly( rayleigh, 'variance', variance );


// EXPORTS //

export default rayleigh;
